import React, { useState, useEffect } from 'react';
import { financeAPI } from '../../lib/api';
import { Save, RefreshCw, Settings, DollarSign } from 'lucide-react';

const OpeningBalanceSettings = () => {
    const [settings, setSettings] = useState({
        Asset: null,
        Liability: null,
        Equity: null,
        Revenue: null,
        Expense: null,
    });
    const [availableAccounts, setAvailableAccounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const accountTypes = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];

    useEffect(() => {
        fetchSettings();
        fetchAvailableAccounts();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await financeAPI.getOpeningBalanceSettings();
            if (response.data.success) {
                setSettings(response.data.data);
            }
        } catch (err) {
            setError('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableAccounts = async () => {
        try {
            const accountsData = {};
            for (const type of accountTypes) {
                const response = await financeAPI.getContraAccounts(type);
                accountsData[type] = response.data.data || [];
            }
            setAvailableAccounts(accountsData);
        } catch (err) {
            console.error('Failed to load available accounts');
        }
    };

    const handleChange = (accountType, value) => {
        setSettings(prev => ({
            ...prev,
            [accountType]: value ? { id: parseInt(value) } : null
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            for (const type of accountTypes) {
                const setting = settings[type];
                if (setting && setting.id) {
                    await financeAPI.saveOpeningBalanceSetting({
                        account_type: type,
                        chart_of_account_id: setting.id
                    });
                }
            }
            setSuccess('Settings saved successfully');
            // Reload to get full account details
            fetchSettings();
            fetchAvailableAccounts();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const getAccountDisplay = (account) => {
        if (!account) return 'Not configured';
        // If we have full account info
        if (account.account_name) {
            return `${account.account_code} - ${account.account_name}`;
        }
        // If we only have ID, try to find in availableAccounts
        for (const type of accountTypes) {
            const found = (availableAccounts[type] || []).find(a => a.id === account.id);
            if (found) {
                return `${found.account_code} - ${found.account_name}`;
            }
        }
        return `ID: ${account.id}`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
        );
    }

    return (
        <div>
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                    <Settings className="w-6 h-6" />
                    Opening Balance Settings
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Configure default contra accounts for opening balances by account type
                </p>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                </div>
            )}

            {success && (
                <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                    {success}
                </div>
            )}

            <div className="bg-white dark:bg-slate-700 rounded-lg shadow overflow-hidden">
                <div className="p-4 bg-indigo-50 dark:bg-indigo-900 border-b border-indigo-100 dark:border-indigo-800">
                    <h2 className="text-lg font-medium text-indigo-900 dark:text-indigo-100 flex items-center gap-2">
                        <DollarSign className="w-5 h-5" />
                        Contra Account Configuration
                    </h2>
                    <p className="text-sm text-indigo-700 dark:text-indigo-300 mt-1">
                        When setting opening balances, these accounts will be used as the opposite entry (credit for assets, debit for liabilities)
                    </p>
                </div>

                <div className="p-6">
                    <div className="grid gap-6">
                        {accountTypes.map(type => (
                            <div key={type} className="flex flex-col sm:flex-row sm:items-center gap-4">
                                <div className="sm:w-32">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        {type}
                                    </span>
                                </div>
                                <div className="flex-1">
                                    <select
                                        value={settings[type]?.id || ''}
                                        onChange={(e) => handleChange(type, e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                    >
                                        <option value="">Select contra account...</option>
                                        {(availableAccounts[type] || []).map(account => (
                                            <option key={account.id} value={account.id}>
                                                {account.account_code} - {account.account_name} ({account.account_type})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sm:w-48 text-sm text-gray-500 dark:text-gray-400">
                                    {settings[type] ? (
                                        <span className="text-green-600 dark:text-green-400">
                                            ✓ Current: {getAccountDisplay(settings[type])}
                                        </span>
                                    ) : (
                                        <span className="text-yellow-600 dark:text-yellow-400">
                                            ⚠ Not configured
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 pt-4 border-t border-gray-200 dark:border-slate-600">
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-lg"
                        >
                            {saving ? (
                                <>
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4 mr-2" />
                                    Save Settings
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <div className="mt-6 bg-blue-50 dark:bg-blue-900 rounded-lg p-4">
                <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                    How it works:
                </h3>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                    <li>• <strong>Asset accounts:</strong> When you set an opening balance, the system will debit the asset account and credit the configured contra account (typically Owner Equity or Capital)</li>
                    <li>• <strong>Liability accounts:</strong> The system will credit the liability account and debit the contra account</li>
                    <li>• <strong>Equity accounts:</strong> Used when setting owner equity opening balances</li>
                    <li>• If not configured, the system will try to find an account with "Owner Equity", "Capital", or "Retained Earnings" in the name</li>
                </ul>
            </div>
        </div>
    );
};

export default OpeningBalanceSettings;
